import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { Button, Label, Select, Switch } from '../../components/ui';
import { ChevronLeftIcon } from '../../components/ui/Icons';
import { fetcher } from '../../services/fetcher';
import api from '../../services/api';
import { moduleConfig } from './config';
import { normalizePaginatedListResponse } from '../../services/paginatedList';

type AccAccountRow = {
  id: number;
  code: string;
  name: string;
  combLevel?: string | null;
};

type PostingConfig = {
  id: number;
  enabled: boolean;
  saveOnClose: boolean;
  wagesExpenseAccountId: number;
  crewPayableAccountId: number;
  allotmentAccountId?: number | null;
  cashAdvanceAccountId?: number | null;
  bondedAccountId?: number | null;
  deductionsAccountId?: number | null;
};

function accountLabel(a: AccAccountRow) {
  return a.name || a.combLevel || a.code;
}

function errMsg(err: unknown, fallback: string) {
  if (err && typeof err === 'object' && 'response' in err) {
    const data = (err as { response?: { data?: { message?: string | string[] } } }).response?.data;
    const m = data?.message;
    if (Array.isArray(m)) return m.join('; ');
    if (typeof m === 'string' && m.trim()) return m;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

const emptyForm = {
  enabled: true,
  saveOnClose: false,
  wagesExpenseAccountId: '',
  crewPayableAccountId: '',
  allotmentAccountId: '',
  cashAdvanceAccountId: '',
  bondedAccountId: '',
  deductionsAccountId: '',
};

const PostingConfigPage: React.FC = () => {
  const { colors } = useTheme();
  const { showError, showSuccess } = useNotification();
  const { can } = useAuth();
  const canEdit = can(moduleConfig.module.permissionModuleKey, 'edit');

  const { data: config, mutate, isLoading } = useSWR<PostingConfig | null>(
    '/mga/posting-config',
    fetcher,
    { revalidateOnFocus: false },
  );

  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!config) return;
    setForm({
      enabled: Boolean(config.enabled),
      saveOnClose: Boolean(config.saveOnClose),
      wagesExpenseAccountId: String(config.wagesExpenseAccountId || ''),
      crewPayableAccountId: String(config.crewPayableAccountId || ''),
      allotmentAccountId: config.allotmentAccountId ? String(config.allotmentAccountId) : '',
      cashAdvanceAccountId: config.cashAdvanceAccountId
        ? String(config.cashAdvanceAccountId)
        : '',
      bondedAccountId: config.bondedAccountId ? String(config.bondedAccountId) : '',
      deductionsAccountId: config.deductionsAccountId ? String(config.deductionsAccountId) : '',
    });
  }, [config]);

  const { data: accountsRaw } = useSWR(
    '/acc-accounts?postingOnly=true',
    fetcher,
    { revalidateOnFocus: false },
  );
  const accounts = useMemo(
    () => normalizePaginatedListResponse<AccAccountRow>(accountsRaw, 1, 5000).data,
    [accountsRaw],
  );
  const accountOptions = useMemo(
    () =>
      accounts.map((a) => ({
        value: String(a.id),
        label: accountLabel(a),
      })),
    [accounts],
  );

  const save = async () => {
    if (!canEdit) return;
    const wagesExpenseAccountId = Number(form.wagesExpenseAccountId);
    const crewPayableAccountId = Number(form.crewPayableAccountId);
    if (!wagesExpenseAccountId || !crewPayableAccountId) {
      showError('Select wages expense and crew payable accounts.');
      return;
    }
    const opt = (v: string) => (v ? Number(v) : null);
    setBusy(true);
    try {
      await api.put('/mga/posting-config', {
        enabled: form.enabled,
        saveOnClose: form.saveOnClose,
        wagesExpenseAccountId,
        crewPayableAccountId,
        allotmentAccountId: opt(form.allotmentAccountId),
        cashAdvanceAccountId: opt(form.cashAdvanceAccountId),
        bondedAccountId: opt(form.bondedAccountId),
        deductionsAccountId: opt(form.deductionsAccountId),
      });
      await mutate();
      showSuccess('MGA posting accounts saved.');
    } catch (err) {
      showError(errMsg(err, 'Save failed.'));
    } finally {
      setBusy(false);
    }
  };

  const accountSelect = (
    label: string,
    hint: string,
    field: keyof typeof emptyForm,
    required?: boolean,
  ) => (
    <div className="space-y-1.5">
      <Label>
        {label}
        {required ? ' *' : ''}
      </Label>
      <Select
        value={form[field] as string}
        onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
        options={accountOptions}
        placeholder="Select account"
        disabled={!canEdit || busy}
        showClearButton={!required}
      />
      <p className={cn('text-xs', colors.textMuted)}>{hint}</p>
    </div>
  );

  return (
    <div className={cn('mx-auto max-w-2xl space-y-6 p-6', colors.background)}>
      <div>
        <Link
          to={moduleConfig.module.route.list}
          className={cn(
            'mb-3 inline-flex items-center gap-1 text-sm',
            colors.primaryFg,
            'hover:underline',
          )}
        >
          <ChevronLeftIcon className="h-4 w-4" />
          MGA periods
        </Link>
        <h1 className={cn('text-xl font-semibold', colors.text)}>Posting settings</h1>
        <p className={cn('mt-1 text-sm', colors.textMuted)}>
          Map wages expense and crew payable accounts so closing an MGA can create an AccDocument
          journal. No database seed required.
        </p>
      </div>

      {isLoading ? (
        <p className={cn('text-sm', colors.textMuted)}>Loading…</p>
      ) : (
        <div className={cn('space-y-5 rounded-lg border p-5', colors.border, colors.surface)}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className={cn('text-sm font-medium', colors.text)}>Enabled</p>
              <p className={cn('text-xs', colors.textMuted)}>
                When off, close locks the period without creating a journal.
              </p>
            </div>
            <Switch
              checked={form.enabled}
              disabled={!canEdit || busy}
              onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className={cn('text-sm font-medium', colors.text)}>Save journal on close</p>
              <p className={cn('text-xs', colors.textMuted)}>
                Otherwise the AccDocument stays DRAFT for review in Accounting.
              </p>
            </div>
            <Switch
              checked={form.saveOnClose}
              disabled={!canEdit || busy}
              onChange={(e) => setForm((f) => ({ ...f, saveOnClose: e.target.checked }))}
            />
          </div>

          {accountSelect(
            'Wages expense',
            'Debited for gross wages on close.',
            'wagesExpenseAccountId',
            true,
          )}
          {accountSelect(
            'Crew payable',
            'Credited for net due (and used when optional accounts are blank).',
            'crewPayableAccountId',
            true,
          )}
          {accountSelect(
            'Allotments (optional)',
            'Credit allotments here; blank folds into crew payable.',
            'allotmentAccountId',
          )}
          {accountSelect(
            'Cash advances (optional)',
            'Office + master cash advances.',
            'cashAdvanceAccountId',
          )}
          {accountSelect('Bonded (optional)', 'Cigarettes / cards / other bonded.', 'bondedAccountId')}
          {accountSelect(
            'Other deductions (optional)',
            'Various and remaining deductions.',
            'deductionsAccountId',
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="default"
              disabled={!canEdit || busy}
              onClick={() => void save()}
            >
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostingConfigPage;
